import React, { useEffect, useRef } from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Close as CloseIcon,
  Description as DescriptionIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { DirectUpload } from "@rails/activestorage";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import AllergyIcon from "@/components/Icons/AllergyIcon";
import FoodIntoleranceIcon from "@/components/Icons/FoodIntoleranceIcon";
import PreferredDietIcon from "@/components/Icons/PreferredDietIcon";
import { graphql } from "@/gql";
import {
  AllergiesKind,
  Attachment,
  CreateDietaryHistoryInput,
  DietaryHistory,
  UpdateDietaryHistoryInput,
} from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { useSnackBar } from "@hooks/useSnackBar";

import MultipleSelect from "./MultipleSelect";

type EditPersonalInfoModalType = {
  visible: boolean | undefined;
  onClose: () => void;
  showSnackBar: (isSuccessful: boolean) => void;
  id: DietaryHistory["id"];
  preferredTypesOfDiet: DietaryHistory["preferredTypesOfDiet"];
  allergies: DietaryHistory["allergies2"];
  foodIntolerance: DietaryHistory["foodIntolerance"];
  files: Partial<Attachment>[];
};
const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  modalBody: {
    backgroundColor: colors.white,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 4),
    width: "50vw",
    maxHeight: "100%",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(12),
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  fieldLabel: {
    fontWeight: "bold",
  },
  save: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    marginBottom: "24px",
    marginTop: "28px",
  },
  error: {
    fontSize: "12px",
    textAlign: "center",
    color: "#963832",
  },
  iconContainer: {
    background: "rgba(31, 212, 175, .1)",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
  uploadButtonContainer: {
    background: colors.white,
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
  icon: {
    width: 20,
    height: 20,
    color: "rgba(125, 135, 151, 1)",
  },

  attachmentsContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  attachment: {
    border: "1px solid #EDF0F5",
  },
  attachmentIcon: {
    fontSize: 32,
    color: "rgba(125, 135, 151, 1)",
  },
  file: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: 100,
    height: 100,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  filePicker: {
    background: "#EDF0F5",
  },
  fileIcon: {
    fontSize: 24,
    color: "rgba(125, 135, 151, 1)",
  },
  fileText: {
    maxWidth: "80%",
    fontSize: 12,
    marginTop: theme.spacing(2),
  },
  removeFileButton: {
    background: "#EDF0F5",
    position: "absolute",
    top: 4,
    right: 4,
  },
  removeFileIcon: {
    fontSize: 16,
  },
}));
const validationSchema = yup.object().shape({
  preferredTypesOfDiet: yup.array().of(yup.string().required()).nullable(),
  allergies: yup
    .array()
    .of(yup.string().required().oneOf(Object.values(AllergiesKind))),
  foodIntolerance: yup.string().nullable(),
});

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.white,
    },
    width: "100%",
    marginTop: "16px",
  },
});

const StyledSaveButton = withStyles(Button, {
  root: {
    backgroundColor: colors.primary,
    width: "132px",
    height: "52px",
    "&:hover": {
      backgroundColor: colors.primary,
    },
  },
});

const TYPES_OF_DIETS = [
  "Alkaline Diet",
  "Anti Inflammatory Diet",
  "Asian Diet",
  "Athletic Diet",
  "Atkins Diet",
  "Baby Food Diet",
  "Balanced Diet",
  "Carb Cycling",
  "DASH Diet (Dietary Approach to Stop Hypertension)",
  "Fasting Diet",
  "Fertility Diet",
  "Flexible Diet",
  "Flexitarian Diet",
  "FODMAP Diet",
  "Gluten Free Diet",
  "Glycemic Index Diet",
  "High Calorie Diet",
  "High Protein - Low Carb Diet",
  "Intermittent Fasting",
  "Keto Diet",
  "Lacto-Ovo Vegetarian Diet",
  "Low Calorie Diet",
  "Low Carb Diet",
  "Low Carb High Fat Diet",
  "Low Carb High Protein Diet",
  "Macrobiotic Diet",
  "Mayo Clinic Diet",
  "Medittaranean Diet",
  "Mind Diet",
  "Modified Keto Diet",
  "Nordic Diet",
  "Ornish Diet",
  "Ovo Vegetarian Diet",
  "Paleo Diet",
  "Personalised DNA Diet",
  "Pescatarian Diet",
  "Plantbased Diet",
  "Rainbow Diet",
  "South Beach Diet",
  "Sugar Detox",
  "Vegan Diet",
  "Vegetarian Diet",
  "Volumetrics Diet",
  "Weight Gain Diet",
  "Weight Watchers Diet",
  "Zone Diet",
];

const ALLERGIES_OPTIONS = Object.values(AllergiesKind);

type FormValues = {
  preferredTypesOfDiet: string[];
  allergies: string[];
  foodIntolerance: string;
  files: Partial<Attachment>[];
};

const dietaryHistoryQuery = graphql(`
  query ClientDietaryHistory($id: ID!) {
    client(id: $id) {
      id
      dietaryHistory {
        ...ClientDietaryHistory
      }
    }
  }
`);

const updateDietaryHistoryMutation = graphql(`
  mutation UpdateDietaryHistory($input: UpdateDietaryHistoryInput!) {
    updateDietaryHistory(input: $input) {
      dietaryHistory {
        ...ClientDietaryHistory
      }
    }
  }
`);

const createDietaryHistoryMutation = graphql(`
  mutation CreateDietaryHistory($input: CreateDietaryHistoryInput!) {
    createDietaryHistory(input: $input) {
      dietaryHistory {
        ...ClientDietaryHistory
      }
    }
  }
`);

const EditDietaryHistoryModal: React.FC<EditPersonalInfoModalType> = ({
  visible,
  onClose,
  showSnackBar,
  id,
  preferredTypesOfDiet,
  allergies,
  foodIntolerance,
  files,
}) => {
  const { classes, cx } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const modalVisibility = visible !== undefined ? visible : false;
  const [updateDietaryHistory] = useMutation(updateDietaryHistoryMutation);
  const [createDietaryHistory] = useMutation(createDietaryHistoryMutation);
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();

  const inputOpenFileRef = useRef<HTMLInputElement>(null);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      allergies: [],
      preferredTypesOfDiet: [],
      files: [],
      foodIntolerance: "",
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: yupResolver(validationSchema),
  });

  const formFiles = watch("files");

  useEffect(() => {
    reset({
      preferredTypesOfDiet: preferredTypesOfDiet ?? [],
      allergies: allergies ?? [],
      foodIntolerance: foodIntolerance ?? "",
      files: files ?? [],
    });
  }, [preferredTypesOfDiet, allergies, foodIntolerance, files]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    try {
      const dietaryHistoryInput = {
        allergies2: formValues?.allergies ?? [],
        preferredTypesOfDiet: formValues?.preferredTypesOfDiet ?? [],
        foodIntolerance: formValues?.foodIntolerance ?? "",
        files: values.files?.length
          ? values.files.map((file) => file.signedId ?? "")
          : [],
      };

      let result;

      // If no dietaryHistory id, create one instead.
      if (id === "") {
        const input: CreateDietaryHistoryInput = {
          clientId: clientId,
          attributes: dietaryHistoryInput,
        };
        result = await createDietaryHistory({
          variables: { input },
          refetchQueries: [
            { query: dietaryHistoryQuery, variables: { id: clientId ?? "" } },
          ],
        });
      } else {
        const input: UpdateDietaryHistoryInput = {
          id,
          patch: dietaryHistoryInput,
        };
        result = await updateDietaryHistory({
          variables: { input },
          refetchQueries: [
            { query: dietaryHistoryQuery, variables: { id: clientId ?? "" } },
          ],
        });
      }

      if (result.data) {
        showSnackBar(true);
      }
    } catch (e) {
      showSnackBar(false);
    } finally {
      onClose();
    }
  };

  const pickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    if (event?.target?.files) {
      const file = event.target.files[0];
      const url = `${import.meta.env.VITE_API_URL}/api/direct_uploads`;
      const upload = new DirectUpload(file, url);
      upload.create((error, blob) => {
        if (error) {
          snackBarHelper.openSnackBar(
            displayError(error, "Error: Cannot upload file.")
          );
        } else {
          const newFile: Partial<Attachment> = {
            filename: blob.filename,
            signedId: blob.signed_id,
            __typename: "Attachment",
          };
          const newFiles = [...formFiles, newFile];
          setValue("files", newFiles);
        }
      });
    }
  };

  const removeFile = (index: number) => {
    //TODO: Implement delete attachment on rails
    const newFiles = formFiles.filter((_, fileIndex) => index !== fileIndex);
    setValue("files", newFiles);
  };

  return (
    <Modal open={modalVisibility} onClose={onClose} className={classes.root}>
      <Box className={classes.modalBody}>
        <Box className={classes.modalHeader}>
          <Typography className={classes.modalHeaderText}>
            Edit Dietary History
          </Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className={classes.fields}>
            <Box marginBottom={8}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <div className={classes.iconContainer}>
                  <PreferredDietIcon className={classes.icon} />
                </div>
                <Typography variant="body1" className={classes.fieldLabel}>
                  Preferred Types of Diet
                </Typography>
              </Box>
              <Controller
                name="preferredTypesOfDiet"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <MultipleSelect
                      values={value}
                      options={TYPES_OF_DIETS}
                      onSelect={(values: string[]) => {
                        onChange(values);
                      }}
                    />
                  );
                }}
              />
            </Box>
            <Box marginBottom={8}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <div className={classes.iconContainer}>
                  <AllergyIcon className={classes.icon} />
                </div>
                <Typography variant="body1" className={classes.fieldLabel}>
                  Allergies
                </Typography>
              </Box>
              <Controller
                name="allergies"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <MultipleSelect
                      values={value}
                      options={ALLERGIES_OPTIONS}
                      onSelect={(values: string[]) => {
                        onChange(values);
                      }}
                    />
                  );
                }}
              />
            </Box>

            <Box marginBottom={8}>
              <Box display="flex" alignItems="center" marginBottom="12px">
                <div className={classes.iconContainer}>
                  <FoodIntoleranceIcon className={classes.icon} />
                </div>
                <Typography variant="body1" className={classes.fieldLabel}>
                  Food Intolerance
                </Typography>
              </Box>
              <Box marginBottom={4}>
                <Typography variant="caption" color="textSecondary">
                  Previous files will be replaced.
                </Typography>
              </Box>
              <Box marginBottom={8}>
                <div className={classes.attachmentsContainer}>
                  <Box
                    component={ButtonBase}
                    className={cx(classes.file, classes.filePicker)}
                    onClick={() => {
                      inputOpenFileRef?.current?.click();
                    }}
                  >
                    <DescriptionIcon className={classes.fileIcon} />
                    <Typography variant="caption">Upload File</Typography>
                  </Box>
                  {formFiles.map((file, index) => (
                    <Box
                      style={{ position: "relative" }}
                      className={cx(classes.file, classes.attachment)}
                      key={index}
                    >
                      <IconButton
                        size="small"
                        className={classes.removeFileButton}
                        onClick={() => removeFile(index)}
                      >
                        <CloseIcon className={classes.removeFileIcon} />
                      </IconButton>
                      <InsertDriveFileIcon className={classes.attachmentIcon} />
                      <Typography
                        variant="body1"
                        noWrap
                        className={classes.fileText}
                      >
                        {file.filename}
                      </Typography>
                    </Box>
                  ))}
                  <input
                    ref={inputOpenFileRef}
                    type="file"
                    accept=".docx, .xslx, .pptx, .pdf"
                    style={{ display: "none" }}
                    onChange={pickFile.bind(this)}
                  />
                </div>
              </Box>
              <Box>
                <Typography variant="caption">Desciption</Typography>
                <Controller
                  name="foodIntolerance"
                  control={control}
                  render={({ field }) => {
                    return <StyledTextField multiline {...field} />;
                  }}
                />
              </Box>
            </Box>

            <div className={classes.save}>
              <StyledSaveButton
                type="submit"
                disabled={!isValid || isSubmitting}
              >
                Update
              </StyledSaveButton>
            </div>
          </Box>
        </form>
        <SimpleSnackBar />
      </Box>
    </Modal>
  );
};
export default EditDietaryHistoryModal;
